<script>
import { mapState } from 'vuex'

// Buyer onboarding
import BuyerProfileName from '@/components/buyer/onboarding/OnboardingProfileName'
import BuyerProfileEmail from '@/components/buyer/onboarding/OnboardingProfileEmail'

const buyerSteps = [
  { id: 0, component: BuyerProfileName },
  { id: 1, component: BuyerProfileEmail }
]

// Seller onboarding
import SellerProfileBasic from '@/components/seller/onboarding/OnboardingProfileBasic'
import SellerProfileSetup from '@/components/seller/onboarding/OnboardingProfileSetup'
import SellerProfileAddress from '@/components/seller/onboarding/OnboardingProfileAddress'
import SellerProfileSchedule from '@/components/seller/onboarding/OnboardingProfileSchedule'
import SellerProfileVerification from '@/components/seller/onboarding/OnboardingProfileVerification'
import SellerProfileTOS from '@/components/seller/onboarding/OnboardingProfileTOS'

const sellerSteps = [
  { id: 0, component: SellerProfileBasic },
  { id: 1, component: SellerProfileSetup },
  { id: 2, component: SellerProfileAddress },
  { id: 3, component: SellerProfileSchedule },
  { id: 4, component: SellerProfileVerification },
  { id: 5, component: SellerProfileTOS }
]

import { ONBOARDING_QUERY } from '@/graph/gql/user.gql'
import { setupProfile } from '@/graph/mutations/user'

export default {
  name: 'OnboardingProfile',
  apollo: {
    user: {
      query: ONBOARDING_QUERY,
      update: data => data.viewer,
      skip() {
        return !!this.user
      },

      // Set currentStep if the user already has onboarding progress
      result({ data }) {
        // Buyer progress
        if (this.$isBuyer) {
          const { firstName, lastName } = data.viewer
          if (!firstName || !lastName) {
            return (this.currentStep = 0)
          }

          const { email } = data.viewer
          if (!email) {
            return (this.currentStep = 1)
          }
        }

        // Seller progress
        else {
          const { firstName, lastName, email } = data.viewer
          if (!firstName || !lastName || !email) {
            return (this.currentStep = 0)
          }

          const { avatar, description } = data.viewer
          if (!avatar || !description) {
            return (this.currentStep = 1)
          }

          const { sellerLocation } = data.viewer
          if (!sellerLocation) {
            return (this.currentStep = 2)
          }

          const { uploads } = data.viewer
          const { national_ID, typhoid_vaccine_card } = uploads || {}
          if (!national_ID || !typhoid_vaccine_card) {
            return (this.currentStep = 4)
          }

          const { agreements } = data.viewer
          const { sellerTos } = agreements || {}
          if (!sellerTos) {
            return (this.currentStep = 5)
          }
        }

        // Looks like the user has already completed Onboarding
        this.finishOnboarding(data.viewer)
      }
    }
  },
  data() {
    return {
      user: null,
      submitting: false,

      currentStep: 0,
      steps: this.$isBuyer ? buyerSteps : sellerSteps
    }
  },
  computed: {
    ...mapState({
      chef: state => state.cart.chef,
      cartEmpty: state => state.cart.items.length === 0
    }),

    redirectedFromCheckout() {
      return this.chef && !this.cartEmpty
    },

    getCurrentComponent() {
      const step = this.steps.find(s => s.id === this.currentStep)
      return step ? step.component : null
    }
  },
  methods: {
    goPrevStep() {
      if (this.currentStep <= this.$isSeller ? 1 : 0) {
        if (this.$isSeller) return this.$router.push({ name: 'SignOut' })
        else return this.$router.go(-1)
      }

      this.currentStep--
    },

    goNextStep() {
      this.currentStep++

      if (this.currentStep === this.steps.length) {
        this.finishOnboarding()
      }
    },

    finishOnboarding(data = this.user) {
      const { get, omit } = this.lodash
      const { firstName, lastName, email } = this.user

      if (this.$isBuyer) {
        // Event tracking
        this.$amplitude.logEvent('Buyer Onboarded', {
          firstName,
          lastName,
          email,
          currencyCode: 'MYR'
        })

        this.$store.commit('user/setProfileCompleted', true)

        return this.$router.replace(
          this.redirectedFromCheckout ? '/checkout' : '/'
        )
      }

      // Event tracking
      const { description, sellerLocation, agreements } = this.user
      this.$amplitude.logEvent('Seller Onboarded', {
        firstName,
        lastName,
        email,
        description,
        location: omit(sellerLocation, ['__typename']),
        agreements: omit(agreements, ['__typename']),
        currencyCode: 'MYR'
      })

      const isMenuEmpty = get(data.sellerProducts, 'length', 0) <= 0
      this.$router.replace({
        name: isMenuEmpty ? 'MenuView' : 'UpcomingOrdersView'
      })
    },

    updateUser(data) {
      if (!data || this.submitting) return
      this.submitting = true

      setupProfile(data)
        .then(res => {
          const { agreements, sellerLocation, sellerSchedule } = data
          const { updateViewer } = res.data

          if (agreements) {
            updateViewer.agreements = agreements
          }
          if (sellerLocation) {
            updateViewer.sellerLocation = sellerLocation
          }
          if (sellerSchedule) {
            updateViewer.sellerSchedule = sellerSchedule
          }

          const { merge, omitBy, isNil } = this.lodash
          this.user = merge(this.user, omitBy(updateViewer, isNil))

          this.goNextStep()
        })
        .finally(() => (this.submitting = false))
    }
  }
}
</script>

<template>
  <transition class="slide-up" appear>
    <v-layout>
      <v-app-bar color="white" flat app>
        <v-btn icon @click="goPrevStep()">
          <v-icon color="black" size="18">$vuetify.icons.arrowBack</v-icon>
        </v-btn>
      </v-app-bar>

      <v-flex xs12 md4 mx-auto mt-12>
        <!-- Loading -->
        <v-layout
          v-if="!user || $apollo.loading"
          align-center
          justify-center
          pt-12
          mt-12
        >
          <v-progress-circular :size="20" color="secondary" indeterminate />
        </v-layout>

        <transition v-if="user && getCurrentComponent" class="fade" appear>
          <component
            :is="getCurrentComponent"
            :user="user"
            :submitting="submitting"
            @goNextStep="goNextStep"
            @goPrevStep="goPrevStep"
            @submit="updateUser"
          />
        </transition>
      </v-flex>
    </v-layout>
  </transition>
</template>

<style scoped></style>
